import type { ChangeEvent } from "react";
import { twJoin } from "tailwind-merge";

import { CheckIcon } from "@/components/Icons/CheckIcon";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { twResolve } from "@/helpers/tw-resolve";

export type CheckboxProps = {
  id?: string;
  name?: string;
  checked?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  shape?: "rounded" | "square";
  color?: "blue" | "green";
  disabled?: boolean;
  className?: string;
  tooltip?: string;
  ref?: React.Ref<HTMLInputElement>;
} & (
  | {
      label?: never;
      alignTop?: never;
    }
  | {
      label: string;
      alignTop?: boolean;
    }
);
export function Checkbox({
  onChange,
  shape = "square",
  color = "blue",
  className,
  ref,
  id,
  name,
  checked,
  disabled,
  label,
  tooltip,
  alignTop,
  ...props
}: CheckboxProps): React.ReactNode {
  return (
    <Tooltip tooltip={tooltip}>
      <label className={twJoin("inline-flex gap-x-2", alignTop ? "items-start" : "items-center")}>
        {label && <span className={twJoin("order-1", disabled && "text-grey-500")}>{label}</span>}
        <div className={twJoin("relative inline-flex size-5 shrink-0", alignTop ? "mt-0.5" : undefined)}>
          <input
            {...props}
            id={id || name}
            ref={ref}
            className={twResolve("peer absolute z-10 size-full opacity-0 disabled:pointer-events-none", className)}
            onChange={onChange}
            type="checkbox"
            checked={checked || false}
          />
          <div
            className={twResolve(
              "flex size-full cursor-pointer items-center justify-center border border-grey-500 bg-white text-white transition-colors peer-focus-visible:ring-1 peer-focus-visible:ring-offset-1 peer-disabled:border-grey-400 peer-disabled:bg-grey-100 peer-checked-disabled:border-grey-300 peer-checked-disabled:bg-grey-300 peer-checked-all-child:block",
              color === "blue"
                ? "peer-checked:border-aop-basic-blue-500 peer-checked:bg-aop-basic-blue-500 peer-hover:bg-grey-100 peer-hover:ring-grey-900 peer-focus-visible:ring-grey-900 peer-checked-hover:border-aop-basic-blue-600 peer-checked-hover:bg-aop-basic-blue-600 peer-checked-focus-visible:ring-aop-basic-blue-600"
                : "border-green-500 peer-checked:bg-green-500 peer-hover:bg-green-100 peer-hover:ring-green-800 peer-focus-visible:ring-green-800 peer-checked-hover:border-green-700 peer-checked-hover:bg-green-700 peer-checked-focus-visible:ring-green-800",
              shape === "rounded" ? "rounded-full" : "rounded-sm",
              disabled && "cursor-default",
            )}
          >
            <CheckIcon className="absolute hidden" />
          </div>
        </div>
      </label>
    </Tooltip>
  );
}
